import {
  actionColumn,
  Button,
  ButtonMenu,
  collapseColumn,
  column,
  Css,
  dateColumn,
  emptyCell,
  FilterDefs,
  Filters,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  multiFilter,
  Pagination,
  ScrollableContent,
  simpleHeader,
  toggleFilter,
  treeFilter,
  useModal,
  usePersistedFilter,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { chipCell, dateCell, SearchBox } from "src/components";
import { LoadingTable } from "src/components/LoadingTable";
import { StatusIndicator } from "src/components/StatusIndicator";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import {
  BidPackageGroupOrderBy,
  BidPackageGroupsFilter,
  BidPackageStatus,
  BidScopeSource,
  Maybe,
  Order,
} from "src/generated/graphqlTypes";
import useZodQueryString from "src/hooks/useZodQueryString";
import {
  BidPackageDetailFragment,
  BidPackageGroupDetailFragment,
  BidPackageRequestDetailFragment,
  ProjectBidPackagesPageMetadataFragment,
  useBidPackageGroupsQuery,
  useBidPackagesPageMetadataQuery,
  useDeleteBidPackageMutation,
} from "src/routes/bids/BidPackagesPage.generated";
import {
  createBidPackagesDetailUrl,
  createBidPackagesEditUrl,
  createBidPackageUrl,
  createDevelopmentProductOfferingUrl,
  createProjectProductOfferingUrl,
} from "src/RouteUrls";
import { groupBy, pageSchema, queryResult } from "src/utils";
import { parseOrder, toOrder } from "src/utils/ordering";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { PageHeader } from "../layout/PageHeader";
import { TableActions } from "../layout/TableActions";
import { useProjectContext } from "../projects/context/ProjectContext";
import { UpdatesAvailableButton } from "./components/BidUpdatesAvailable";

export function BidPackagesPage() {
  // Loading markets, projects, and bid categories before rendering the page
  const query = useBidPackagesPageMetadataQuery();
  return queryResult(query, {
    data: ({ costCodes, projects }) => <BidPackagesPageView projects={projects} costCodes={costCodes} />,
  });
}

export function ProjectBidPackagesPage() {
  const query = useBidPackagesPageMetadataQuery();
  return queryResult(query, {
    data: ({ costCodes }) => <BidPackagesPageView costCodes={costCodes} />,
  });
}

export type BidPackagesPageViewProps = {
  costCodes: NamedFragment[];
  projects?: ProjectBidPackagesPageMetadataFragment[];
};

export function BidPackagesPageView({ costCodes, projects }: BidPackagesPageViewProps) {
  const project = useProjectContext();
  // This component can be used within a developments and projects
  const { projectId, developmentId } = useParams<{ projectId?: string; developmentId?: string }>();
  const { openModal } = useModal();
  const [deleteBidPackage] = useDeleteBidPackageMutation({
    refetchQueries: ["BidPackageGroups"],
  });
  const [pageSettings, setPageSettings] = useZodQueryString(pageSchema);
  const [order, setOrder] = useState<BidPackageGroupOrderBy>({ updatedAt: Order.Desc });
  const filterDefs: FilterDefs<BidPackageGroupsFilter> = useMemo(
    () => ({
      ...(Array.isArray(projects)
        ? {
            project: treeFilter({
              defaultCollapsed: true,
              label: "Markets and Communities",
              filterBy: "leaf",
              getOptionLabel: (o) => o.name,
              getOptionValue: (o) => o.id,
              options: Object.values(
                groupBy(
                  projects.filter((p) => p.market),
                  (p) => p.market!.id,
                ),
              ).map((p) => ({
                id: p[0].market!.id,
                name: p[0].market!.name,
                children: p.map((d) => ({ id: d.id, name: d.name })),
              })),
            }),
          }
        : {}),
      costCode: multiFilter({
        label: "Cost Code",
        getOptionLabel: (o) => o.name,
        getOptionValue: (o) => o.id,
        options: costCodes,
      }),
      // If we are viewing project bid packages, include the toggle to show bids from all levels
      ...(project.id
        ? {
            includeChildProjectBids: toggleFilter({
              label: "Show Bids from All Levels",
              defaultValue: true,
            }),
          }
        : {}),
    }),
    [projects, costCodes],
  );

  const { filter, setFilter } = usePersistedFilter<BidPackageGroupsFilter>({
    storageKey: "bidPackageGroupsFilter",
    filterDefs,
  });
  const [searchFilter, setSearchFilter] = useState<string>("");

  const { includeChildProjectBids, ...additionalFilters } = filter;

  const { loading, data } = useBidPackageGroupsQuery({
    variables: {
      filter: {
        search: searchFilter || undefined,
        development: developmentId ? [developmentId] : undefined,
        project: projectId ? [projectId] : undefined,
        // If we are viewing subdivision bid packages, include child project bids toggle determines if Lot bids are included
        ...(project.__typename === "Subdivision"
          ? {
              scopeSource: includeChildProjectBids
                ? [BidScopeSource.Subdivision, BidScopeSource.Lot]
                : [BidScopeSource.Subdivision],
            }
          : { includeChildProjectBids }),
        ...additionalFilters,
      },
      orderBy: order,
      packageOrderBy: order,
      page: pageSettings,
      costCode: filter.costCode,
    },
  });

  const onDelete = useCallback(
    (bidPackage: BidPackageDetailFragment) => {
      openModal({
        content: (
          <ConfirmationModal
            confirmationMessage={
              <>
                Are you sure you want to delete this bid package <b>{bidPackage.name}</b>? It will not be archived and
                cannot be undone.
              </>
            }
            onConfirmAction={async () => {
              await deleteBidPackage({ variables: { id: bidPackage.id } });
            }}
            title="Delete Bid Package"
            label="Yes, Delete"
          />
        ),
      });
    },
    [deleteBidPackage],
  );

  const columns = useMemo(
    () => createColumns(onDelete, developmentId, projectId),
    [projectId, developmentId, onDelete],
  );
  const rows = useMemo(() => createRows(data?.bidPackageGroups.entities || []), [data?.bidPackageGroups]);

  return (
    <div>
      <PageHeader
        title="Bid Packages"
        right={
          <>
            <ButtonMenu
              trigger={{ label: "Create Bids", variant: "primary" }}
              items={[
                {
                  label: "Create New Bid",
                  onClick: createBidPackageUrl("add", { developmentId, projectId }),
                },
                {
                  label: "Enter Historical Bid",
                  onClick: createBidPackageUrl("add", { developmentId, projectId, isHistorical: true }),
                },
                {
                  label: "Enter Internal Estimate",
                  onClick: createBidPackageUrl("add", { developmentId, projectId, isInternalEstimate: true }),
                },
              ]}
            />
          </>
        }
      />
      <TableActions>
        <Filters filter={filter} onChange={setFilter} filterDefs={filterDefs} />
        <SearchBox onSearch={setSearchFilter} />
      </TableActions>
      <ScrollableContent>
        {!loading ? (
          <>
            <GridTable
              columns={columns}
              rows={rows}
              sorting={{
                on: "server",
                onSort: (key, direction) => setOrder(toOrder(key, direction)),
                value: parseOrder(order),
              }}
              stickyHeader
              fallbackMessage="There aren't any bid packages to display."
            />
            <Pagination
              page={[pageSettings, setPageSettings]}
              totalCount={data?.bidPackageGroups.pageInfo.totalCount || 0}
            />
          </>
        ) : (
          <LoadingTable />
        )}
      </ScrollableContent>
    </div>
  );
}

type HeaderRow = { kind: "header" };
type BidPackageGroupedRow = { kind: "group"; data: BidPackageGroupDetailFragment; children: BidPackageRow[] };
type BidPackageRow = { kind: "package"; data: BidPackageDetailFragment; children: BidPackageRequestRow[] };
type BidPackageRequestRow = { kind: "request"; data: BidPackageRequestDetailFragment };
type Row = HeaderRow | BidPackageGroupedRow | BidPackageRow | BidPackageRequestRow;

const createColumns = (
  onDelete: (bidPackage: BidPackageDetailFragment) => void,
  developmentId?: string,
  projectId?: string,
): GridColumn<Row>[] => [
  collapseColumn<Row>(),
  column<Row>({
    header: "Name",
    group: ({ id, status, displayName, bidPackages }) =>
      // Allow edit if the status is draft
      status.code === BidPackageStatus.Draft && bidPackages.every((bp) => bp.latestVersion.version === 1) ? (
        <Button
          variant="text"
          onClick={createBidPackagesEditUrl(id, developmentId, projectId)}
          label={<span css={Css.mdSb.$}>{displayName}</span>}
        />
      ) : (
        {
          content: displayName,
          css: Css.mdSb.$,
        }
      ),
    package: ({ name, bidPackageGroup, id, latestVersion, availableUpdates, status, costCodesName }) => ({
      content:
        latestVersion.version !== 1 || status.code !== BidPackageStatus.Draft ? (
          <div css={Css.df.fdr.aic.gap2.$}>
            <Button
              variant="text"
              label={<FormattedBidName costCodesName={costCodesName} latestVersion={latestVersion} />}
              onClick={createBidPackagesDetailUrl(bidPackageGroup!.id, id)}
            />
            <UpdatesAvailableButton bidPackageId={id} updates={availableUpdates} showText={false} />
          </div>
        ) : (
          <div css={Css.df.fdr.aic.gap2.$}>
            <FormattedBidName costCodesName={costCodesName} latestVersion={latestVersion} />
            <UpdatesAvailableButton bidPackageId={id} updates={availableUpdates} showText={false} />
          </div>
        ),
      value: name,
      w: 2,
      css: Css.sm.$,
    }),
    request: ({ tradePartner }) => ({
      content: tradePartner.name,
      w: developmentId || projectId ? 2 : 3,
      typeScale: "xs",
    }),
  }),
  column<Row>({
    header: "Cost Type",
    group: emptyCell,
    package: ({ costType }) => costType.name,
    request: emptyCell,
  }),
  column<Row>({
    header: "Product Offerings",
    group: emptyCell,
    package: ({ latestVersion, developments, projects }) => {
      const rpavCount = latestVersion.readyPlanAggregateVersions.length;
      return {
        content: (
          <div css={Css.df.fdc.$}>
            {latestVersion.readyPlanAggregateVersions.map((rpav, idx) => {
              return (
                <div key={rpav.id} css={Css.df.fdr.gap1.mx2.$}>
                  <span>{rpav.readyPlan.name}</span>
                  {/** TODO: We currently on support one development per Bid.
                   * In the future may need to update this cell to map through all the rpav developments to produce the correct URL
                   */}
                  <span>
                    <Link
                      to={
                        developments.nonEmpty
                          ? createDevelopmentProductOfferingUrl(developments[0].id, rpav.readyPlan.id, rpav.id)
                          : createProjectProductOfferingUrl(projects[0].id, rpav.readyPlan.id, rpav.id)
                      }
                    >
                      {rpav.displayVersion}
                    </Link>
                    {idx < rpavCount - 1 && <span css={Css.mr1.$}>,</span>}
                  </span>
                </div>
              );
            })}
          </div>
        ),
        w: 2.5,
      };
    },
    request: emptyCell,
  }),
  ...(!developmentId
    ? [
        column<Row>({
          header: "Hierarchy",
          group: ({ developments, projects, bidPackages }) =>
            bidHierarchyColumn({ developments, projects, scopeSource: bidPackages.first!.scopeSource }),
          package: ({ developments, projects, latestVersion, status, scopeSource }) =>
            latestVersion.version !== 1 || status.code !== BidPackageStatus.Draft
              ? bidHierarchyColumn({ developments, projects, scopeSource })
              : emptyCell,
          request: emptyCell,
        }),
      ]
    : []),
  column<Row>({
    header: "Status",
    group: ({ status }) => (
      <div css={Css.df.aic.$}>
        <StatusIndicator status={status.code} type="BidPackage" />
        <span css={Css.ml1.$}>{status.name}</span>
      </div>
    ),
    package: ({ status }) => (
      <div css={Css.df.aic.$}>
        <StatusIndicator status={status.code} type="BidPackage" />
        <span css={Css.ml1.$}>{status.name}</span>
      </div>
    ),
    request: emptyCell,
    serverSideSortKey: "status",
  }),
  dateColumn<Row>({
    header: "Last Edited",
    group: ({ updatedAt }) => dateCell(updatedAt),
    package: ({ updatedAt }) => dateCell(updatedAt),
    request: emptyCell,
    serverSideSortKey: "updatedAt",
  }),
  actionColumn<Row>({
    header: emptyCell,
    group: emptyCell,
    package: (data) => ({
      revealOnRowHover: true,
      content: data.canDelete.allowed && <IconButton inc={2} icon="trash" onClick={() => onDelete(data)} />,
    }),
    request: emptyCell,
    w: "80px",
  }),
];

function createRows(bidPackageGroups: BidPackageGroupDetailFragment[]): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...bidPackageGroups.flatMap((bpg) => {
      const firstBidPackage = bpg.bidPackages.first!;
      // If we have published the first bid package don't show the group, this is because the groups are purely used for bulk bid package creation
      if (firstBidPackage.latestVersion.version !== 1 || firstBidPackage.status.code !== BidPackageStatus.Draft) {
        return bpg.bidPackages.map(
          (bp): GridDataRow<Row> => ({
            kind: "package" as const,
            id: bp.id,
            data: bp,
            children: bp.activeRequests.map((r) => ({
              kind: "request" as const,
              id: r.id,
              data: r,
            })),
          }),
        );
      }
      return [
        {
          kind: "group" as const,
          id: bpg.id,
          data: bpg,
          children: bpg.bidPackages.map((bp) => ({
            kind: "package" as const,
            id: bp.id,
            data: bp,
            children: bp.activeRequests.map((r) => ({
              kind: "request" as const,
              id: r.id,
              data: r,
            })),
          })),
        },
      ];
    }),
  ];
}

// Format the long bid name and versions labels
// To fit fixed width columns
export function FormattedBidName({
  costCodesName,
  latestVersion,
}: {
  costCodesName: string;
  latestVersion: { displayVersion: string };
}) {
  return (
    <div css={Css.df.fdc.$}>
      <div css={Css.df.fdc.wsnw.$}>
        {costCodesName.split(",").map((n) => (
          <span key={n}>{n}</span>
        ))}
      </div>
      <span css={Css.xs.$}>{latestVersion.displayVersion}</span>
    </div>
  );
}

// For now we don't want to list out all lots or have chips saying "All Lots in subdivision"
export function bidHierarchyColumn({
  developments,
  projects,
  scopeSource,
}: Pick<BidPackageDetailFragment, "developments" | "projects"> & {
  scopeSource: Maybe<BidPackageDetailFragment["scopeSource"]>;
}) {
  return scopeSource?.code === BidScopeSource.Lot
    ? chipCell(["Lot"])
    : chipCell(projects.nonEmpty ? projects.map((p) => p.name) : developments.map((d) => d.name));
}
